/**
 * LaunchDarkly flag keys and variant names for marketing-site experiments.
 *
 * Flag keys are shared with the LaunchDarkly dashboard — renaming one here
 * without renaming it there silently falls the site back to the control.
 */

// Every flag the client resolves on boot. Add to this list rather than calling
// `variation()` ad hoc, so there is one place to see what the site evaluates.
// The homepage messaging test ended on 2026-10-07 (BIO-2166); nothing is under test today.
export const ExperimentFlags: {
  key: string;
  experiment: string;
  fallback: string;
}[] = [];

/**
 * The SuperVision ladder, grouped the way the B-side sells it.
 *
 * A marketing card is a *level*, not a plan. Clicking one enters the funnel scoped to that
 * level, where the visitor chooses between the two plans inside it. Order matters: the first
 * entry is the cheaper plan and the one whose price the marketing card quotes as "from $X".
 *
 * Prices are not here on purpose — they come from Stripe via the core-plan catalog. This only
 * says which productKeys belong together. See SUP-8 for the ladder, BIO-1706 for the funnel.
 */
export const SupervisionLevel = {
  OBSERVATION: 'observation',
  SUPERVISION: 'supervision',
  MANAGEMENT: 'management',
} as const;

export type SupervisionLevelValue =
  typeof SupervisionLevel[keyof typeof SupervisionLevel];

export const SUPERVISION_LEVEL_PRODUCT_KEYS: Record<string, string[]> = {
  [SupervisionLevel.OBSERVATION]: [
    'COMPANY_OBSERVATION',
    'WORKFORCE_OBSERVATION',
  ],
  [SupervisionLevel.SUPERVISION]: [
    'STANDARD_SUPERVISION',
    'PRACTICE_SUPERVISION',
  ],
  [SupervisionLevel.MANAGEMENT]: [
    'WORKFORCE_PRACTICE_MANAGEMENT',
    'ORGANIZATIONAL_MATTER_MANAGEMENT',
  ],
};

/** Every SuperVision productKey, in ladder order. */
export const SUPERVISION_PRODUCT_KEYS: string[] = Object.values(
  SUPERVISION_LEVEL_PRODUCT_KEYS
).flat();

export const levelForProductKey = (productKey?: string): string | undefined =>
  Object.keys(SUPERVISION_LEVEL_PRODUCT_KEYS).find((level) =>
    SUPERVISION_LEVEL_PRODUCT_KEYS[level].includes(productKey || '')
  );

export default {
  ExperimentFlags,
  SUPERVISION_LEVEL_PRODUCT_KEYS,
  SUPERVISION_PRODUCT_KEYS,
  SupervisionLevel,
  levelForProductKey,
};
